import { JobSpeciality, JobType } from '@clipr/lib';
import { action, makeObservable, observable, override, runInAction } from 'mobx';
import { assertNotNull, Result, sleep } from '../../core';
import { JobModel } from '../../entities';
import { Store } from '../../store/store';
import { JobLevel } from '../../entities/job/jobFeatures';
import { notifySuccess } from '../notifications';
import { ApiMultipartUploadRequest, ApiMultipartUploadRequestParams } from '../../api/apiMultipartUploadRequest';
import { isConnectionError, UploadTask } from './uploadTask';

/**
 * Parts are sized to keep a dropped connection cheap: a part lost at 99% costs its own size in
 * re-sent bytes, and the 250MB parts this used to send made every blip expensive.
 */
const MIN_CHUNK_SIZE = 1024 * 1024 * 16;

/**
 * Bounds the presigned-url list AppSync has to return in one payload, and the sequential signing
 * loop behind it. Measured against staging: a signed part url is ~1.9KB (most of it the STS
 * session token), so 259 parts came back as a 474KB response and took 2.3s to mint. That puts
 * this cap at roughly half of AppSync's 1MB response limit; 500 would have been 89% of it.
 *
 * ponytail: raising this trades headroom on that limit for finer parts on very large files.
 */
const MAX_PARTS = 250;

/**
 * Concurrency is deliberately low. Saturating an agency uplink with parallel streams is what
 * knocked the connection over in the first place, and more streams do not make a capped link
 * faster.
 */
const DEFAULT_THREADS = 2;

const MAX_PART_ATTEMPTS = 4;
const RETRY_BASE_DELAY_MS = 1000;

export type MultipartUploadTaskProps = {
  file: File;
  teamId?: string;
  jobId?: string;
  languageCode?: string;
  isPublic?: boolean;
  jobLevel?: JobLevel;
  jobSpeciality?: JobSpeciality;
  customSpecialtyId?: string;
  videoType?: JobType;
  sourceLanguageCode?: string;
  targetLanguageCode?: string;
  detectiveInsights?: string;
  uploadOnly?: boolean;
  partIndex?: number;
  chunkSize?: number;
  threadsQuantity?: number;
  retryBaseDelayMs?: number;
};

export class MultipartUploadTask
  extends UploadTask {
  nodeType = 'MultipartUploadTask';

  readonly file: File;
  readonly chunkSize: number = 1024 * 1024 * 5;
  readonly threadsQuantity: number = 5;
  readonly retryBaseDelayMs: number = RETRY_BASE_DELAY_MS;

  uploadToken: string | null = null;
  uploadId: string | null = null;
  signedUrls: string[] = [];
  // Using Map to prevent duplicate entries for the same part number
  uploadedParts: Map<number, string> = new Map();

  @observable activeConnections: { [part: number]: ApiMultipartUploadRequest } = {};
  @observable progressCache: {[part: number]: ApiMultipartUploadRequest } = {};

  @override
  get progress() {
    // An interruption keeps its last reading: resetting to 0% reads as if nothing had been sent.
    if (!this.isActive && !this.isInterrupted)
      return 0;

    const total = this.file.size;
    const uploaded = Object.values(this.progressCache).reduce((acc, request) => acc + request.uploadLoaded, 0);

    return total > 0 && uploaded > 0 ? uploaded / total : 0;
  }

  constructor(props: MultipartUploadTaskProps, store: Store) {
    super(props, store);
    makeObservable(this);

    this.file = props.file;
    this.chunkSize = props.chunkSize || Math.max(MIN_CHUNK_SIZE, Math.ceil(props.file.size / MAX_PARTS));
    this.threadsQuantity = Math.min(props.threadsQuantity || DEFAULT_THREADS, 15);
    this.retryBaseDelayMs = props.retryBaseDelayMs ?? RETRY_BASE_DELAY_MS;
  }

  @override
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- return type varies between void and Result<JobModel> in different code paths
  async start(): Promise<any> {
    this.emit('start');
    this.status = 'fetchingUploadInfo';

    const { file, store } = this;
    const { apiService: api } = store;

    const numberOfParts = Math.ceil(this.file.size / this.chunkSize);
    const [prepareRes, prepareErr] = await api.createMultipartUpload({
      input: {
        numberOfParts,
        fileName: file.name,
        teamId: this.teamId!,
        jobId: this.jobId!,
        partIndex: this.partIndex ?? undefined
      }
    });

    if (prepareErr)
      return this.handleError(prepareErr, 'Failed to prepare upload.');

    const res = prepareRes?.createMultipartUpload;
    if (!res?.uploadToken) {
      // Returning silently would strand the task in 'fetchingUploadInfo', and anything waiting on
      // it to settle (a multi-segment submit) would wait forever.
      return this.handleError(new Error('No upload token'), 'Failed to prepare upload.');
    }

    this.synchronizer.bindToken(res.uploadToken);

    assertNotNull(prepareRes);
    this.signedUrls = res.signedUrls;
    this.uploadToken = res.uploadToken;
    this.uploadId = res.uploadId;

    if (this.signedUrls.length)
      this.sendNext();

    // `prepareErr` is provably falsy here -- the truthy case returned above -- and a task that
    // fails with no error object reports canRetry false, so the row offers neither Retry nor
    // Remove.
    else
      return this.handleError(new Error('No signed urls'), 'Failed to prepare upload.');
  }

  @action
  async sendNext() {
    // A part that finishes after the task has already failed still calls back in here. Opening a
    // fresh part would put a terminal task back into 'uploading', and since a failed part keeps
    // its connection slot forever, nothing would ever complete it again.
    if (this.isSettled)
      return;

    const activeConnections = Object.keys(this.activeConnections).length;

    if (activeConnections >= this.threadsQuantity) {
      return;
    }
  
    if (!this.signedUrls.length) {
      if (!activeConnections) {
        return this.complete();
      }

      return;
    }

    const part = this.signedUrls.length;
    const signedUrl = this.signedUrls.pop();

    if (this.file && signedUrl) {
      const sentSize = (part - 1) * this.chunkSize;
      const chunk = this.file.slice(sentSize, sentSize + this.chunkSize);

      const sendChunkStarted = () => {
        this.sendNext()
      }

      this.upload(chunk, signedUrl, part, sendChunkStarted)
      .then(() => {
        this.sendNext();
      })
      .catch((error: unknown) => this.handleFailure(error));
    }
  }

  @override
  cancel() {
    this.isUserCanceled = true;
    Object.values(this.activeConnections).forEach((activeConnection) => activeConnection.cancel());
  }

  /**
   * A retry starts a brand new multipart upload, so nothing from the previous one may survive:
   * its eTags belong to an upload id S3 is about to forget, and completing with them fails with
   * InvalidPart. The connection slots matter just as much -- a part that died mid-flight is
   * never removed from `activeConnections`, so leaving them behind would keep `sendNext` at its
   * thread limit and the retry would sit there sending nothing.
   *
   * Resuming into the existing upload instead of restarting is a separate change; this only
   * makes sure the restart is clean.
   */
  @override
  async retry() {
    this.uploadId = null;
    this.uploadToken = null;
    this.signedUrls = [];
    this.uploadedParts.clear();
    this.activeConnections = {};
    this.progressCache = {};

    return super.retry();
  }

  /**
   * True once this part belongs to an upload the task has moved on from. A retry mints a brand
   * new multipart upload, and a part still sleeping off a backoff from the previous one must not
   * wake up and write its connection, its progress or its eTag into the new one.
   */
  private isStalePart(uploadId: string | null) {
    return this.isSettled || this.uploadId !== uploadId;
  }

  async upload(chunk: Blob, signedUrl: string, part: number, sendChunkStarted: () => void): Promise<void> {
    const { apiService: api } = this.store;
    const uploadId = this.uploadId;

    for (let attempt = 1; ; attempt++) {
      const multipartUploadParams: ApiMultipartUploadRequestParams = {
        part,
        blob: chunk,
        url: signedUrl,
        fileSize: this.file.size,
        contentType: this.file.type
      }

      const uploadRequest = api.multipartUploadRequest(multipartUploadParams);

      runInAction(() => {
        this.status = 'uploading';
        this.activeConnections[part - 1] = uploadRequest;
        this.progressCache[part - 1] = uploadRequest;
      });

      // Only the first attempt opens the next part; a retry must not widen the thread pool.
      if (attempt === 1)
        sendChunkStarted();

      const [uploadRes, uploadErr] = await uploadRequest.start();

      if (!uploadErr) {
        const eTag = uploadRes.headers['etag']?.replaceAll('"', "");

        // No eTag means no proof the part landed. Recording it as a success sends `undefined`
        // into `eTags: [String!]!` after the whole file has already been uploaded; failing here
        // instead lets the retry ladder and the error message describe what actually happened.
        if (!eTag)
          throw new Error(`No ETag returned for part ${part}.`);

        this.uploadedParts.set(part, eTag);
        runInAction(() => delete this.activeConnections[part - 1]);
        return;
      }

      // Only a dropped connection is worth re-sending. A cancellation or a real HTTP status
      // would fail the same way every time.
      if (!isConnectionError(uploadErr) || attempt >= MAX_PART_ATTEMPTS || this.isStalePart(uploadId))
        throw uploadErr;

      await sleep(this.retryBaseDelayMs * 2 ** (attempt - 1));

      // The task can settle -- or be retried into a whole new upload -- while we back off.
      if (this.isStalePart(uploadId))
        throw uploadErr;
    }
  }

  @action
  async complete() {
    if (this.isSettled)
      return [null, this.error] as Result<JobModel>;

    if (!this.uploadId || !this.uploadToken || !this.uploadedParts.size) {
      return this.handleError(new Error('InternalError'), 'Failed to upload file.');
    }

    const { store } = this;
    const { apiService: api } = store;
    const uploadId = this.uploadId;

    // Sort by part number and extract eTags in order
    const eTags = [...this.uploadedParts.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([, eTag]) => eTag);

    const [, completeErr] = await api.completeMultipartUpload({
      input: {
        uploadId,
        uploadToken: this.uploadToken,
        eTags
      }
    });

    // The task can fail while this request is in flight. Writing 'completed' over that would
    // turn a row the user was told had failed into a green one.
    if (this.isStalePart(uploadId))
      return [null, this.error] as Result<JobModel>;

    if (completeErr)
      return this.handleError(completeErr, 'Failed to complete upload.');

    this.status = 'completed';
    return this.syncJob();
  }

  /**
   * Stopping one part means stopping the rest, and those cancellations come back as failures of
   * their own. Only the first one describes what actually happened, so it settles the task and
   * the stragglers are dropped -- otherwise a lost connection ended up reported as 'canceled',
   * once per part still in flight.
   */
  @action
  handleFailure(err: unknown): Result<JobModel> {
    if (this.isSettled)
      return [null, err];

    const result = super.handleFailure(err);
    Object.values(this.activeConnections).forEach((activeConnection) => activeConnection.cancel());

    return result;
  }

  @action
  handleError(err: unknown, errMsg: string): Result<JobModel> {
    this.error = err;
    this.errorMessage = errMsg;
    this.status = 'error';
    this.emit('error');
    this.dispatch(
      'NotificationService',
      'notifyError',
      `Could not upload '${this.file.name}'.`);

    return [null, err];
  }

  @action
  handleCanceled(err: Error): Result<JobModel> {
    this.status = 'canceled';
    this.emit('cancel');
    notifySuccess(this, 'Upload canceled.');
    return [null, err];
  }
}